import React, { useState, useEffect } from 'react';
import { Button, Form } from 'react-bootstrap';
import StyledModal from '../Components/StyledModal';

export const ExportModal = ({
  show,
  onHide,
  includeData,
  onExport, // exportProcess() function reference
  isMailingList = false,
}) => {
  const [isOpen, setIsOpen] = useState(show);
  const [policiesOnNewRow, setPoliciesOnNewRow] = useState(false);
  const [nameLayout, setNameLayout] = useState('fullName');
  const [householdRows, setHouseholdRows] = useState('perHousehold');
  const [isExporting, setIsExporting] = useState(false);
  const [exportType, setExportType] = useState('');
  const [exportContent, setExportContent] = useState(null);

  useEffect(() => {
    if (isMailingList) {
      setExportType('mailing');
    } else if (includeData?.includes('policies')) {
      setExportType('policies');
    } else {
      setExportType('csv');
    }
  }, [includeData, isMailingList]);

  const handleExport = () => {
    setIsExporting(true);
    setExportContent(null);

    if (exportType === 'mailing') {
      onExport(includeData, isExporting, policiesOnNewRow, nameLayout, householdRows)
        .then((html) => {
          setExportContent(html);
        })
        .catch((error) => {
          setExportContent(
            '<div class="alert small alert-danger">Export error: ' + error.message + '</div>'
          );
        });
    } else {
      onExport(includeData, isExporting, policiesOnNewRow)
        .then((html) => {
          setExportContent(html);
        })
        .catch((error) => {
          setExportContent(
            '<div class="alert small alert-danger">Export error: ' + error.message + '</div>'
          );
        });
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    if (onHide) onHide();
  };

  const renderBasicExport = () => (
    <div className="exportModalBody">
      <Button
        variant="primary"
        size="sm"
        className="mt-2"
        onClick={handleExport}
        disabled={isExporting}
      >
        {isExporting ? (
          <>
            <span
              className="spinner-border spinner-border-sm me-2"
              role="status"
              aria-hidden="true"
            ></span>
            Generating Report...
          </>
        ) : (
          'Generate Report'
        )}
      </Button>
    </div>
  );

  const renderPoliciesExport = () => (
    <div className="exportModalBody">
      <Form.Check
        type="checkbox"
        id="policiesNewRow"
        label={
          <>
            Display multiple policies on new rows?
            <br />
            <small className="text-muted">
              (this will create duplicate profile rows but with different policy data)
            </small>
          </>
        }
        checked={policiesOnNewRow}
        onChange={(e) => setPoliciesOnNewRow(e.target.checked)}
        className="mb-3"
      />
      <Button
        variant="primary"
        size="sm"
        className="mt-2"
        onClick={handleExport}
        disabled={isExporting}
      >
        {isExporting ? (
          <>
            <span
              className="spinner-border spinner-border-sm me-2"
              role="status"
              aria-hidden="true"
            ></span>
            Generating Report...
          </>
        ) : (
          'Generate Report'
        )}
      </Button>
    </div>
  );

  const renderMailingListExport = () => (
    <div className="exportModalBody">
      <div className="row g-3">
        <div className="col-12 mt-2">
          <Form.Label htmlFor="nameColumns">Name columns</Form.Label>
          <Form.Select
            id="nameColumns"
            size="sm"
            value={nameLayout}
            onChange={(e) => setNameLayout(e.target.value)}
            className="form-control form-control-sm"
          >
            <option value="fullName">
              Single column — Full name (e.g., &quot;John Smith&quot;)
            </option>
            <option value="firstLast">Two columns — First, Last</option>
          </Form.Select>
          <Form.Text className="text-muted">How the person&apos;s name is exported.</Form.Text>
        </div>

        {nameLayout === 'fullName' && (
          <div className="col-12 mt-3">
            <Form.Label htmlFor="householdRows">Household rows</Form.Label>
            <Form.Select
              id="householdRows"
              size="sm"
              value={householdRows}
              onChange={(e) => setHouseholdRows(e.target.value)}
              className="form-control form-control-sm"
            >
              <option value="perHousehold">
                One row per household — Combine spouses (e.g., &quot;John & Jane Smith&quot;)
              </option>
              <option value="perPerson">One row per person — Separate spouse rows</option>
            </Form.Select>
            <Form.Text className="text-muted">
              Whether spouses share a row (only applies to single-column names).
            </Form.Text>
          </div>
        )}
      </div>

      <div className="d-flex justify-content-end mt-3">
        <Button variant="primary" size="sm" onClick={handleExport} disabled={isExporting}>
          {isExporting ? (
            <>
              <span
                className="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              ></span>
              Generating Report...
            </>
          ) : (
            'Generate Report'
          )}
        </Button>
      </div>
    </div>
  );

  const getTitle = () => {
    if (exportType === 'mailing') return 'Export Mailing List';
    if (exportType === 'policies') return 'Export Policies';
    return 'Export CSV';
  };

  const renderContent = () => {
    if (exportContent) {
      return <div className="export-content" dangerouslySetInnerHTML={{ __html: exportContent }} />;
    }

    if (isExporting) {
      return (
        <div className="alert small alert-info m-0">
          <i className="fas fa-spinner fa-spin me-2"></i>
          Your export is being generated.
        </div>
      );
    }

    switch (exportType) {
      case 'mailing':
        return renderMailingListExport();
      case 'policies':
        return renderPoliciesExport();
      default:
        return renderBasicExport();
    }
  };

  return (
    <StyledModal
      size="md"
      title={getTitle()}
      show={isOpen}
      onHide={onHide}
      onClose={handleClose}
      footerCloseButton={true}
    >
      {renderContent()}
    </StyledModal>
  );
};

export default ExportModal;
